import { useEffect, useRef } from 'react';

import * as CookieConsent from 'vanilla-cookieconsent';

import { setConsentModalOpen } from './consentModalStore';
import { cookieConsentConfig, updateGAConsent, isDoNotTrackEnabled } from './cookieConsentConfig';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

/**
 * CookieConsent Component
 *
 * GDPR-compliant cookie consent banner using vanilla-cookieconsent library.
 *
 * Features:
 * - Display on first visit
 * - Options: Accept All, Reject All
 * - Categories: Essential (always on), Analytics, Marketing
 * - Persists consent choice in localStorage
 * - Respects Do Not Track (DNT) header
 * - Integrates with GA4 consent mode
 *
 * @example
 * ```tsx
 * import { CookieConsent } from '@/components/CookieConsent';
 *
 * function App() {
 *   return (
 *     <>
 *       <CookieConsent />
 *       <YourAppContent />
 *     </>
 *   );
 * }
 * ```
 */
const CookieConsentComponent = () => {
  const initialized = useRef(false);

  useEffect(() => {
    // Prevent double initialization
    if (initialized.current) {
      return;
    }

    initialized.current = true;

    // Check DNT status
    const dntEnabled = isDoNotTrackEnabled();

    // Initialize cookie consent
    CookieConsent.run({
      ...cookieConsentConfig,

      // Handle consent changes
      onChange: ({ cookie }) => {
        const acceptedCategories = cookie.categories || [];

        // Update GA4 consent mode
        updateGAConsent(acceptedCategories);
      },

      // Handle first consent
      onFirstConsent: ({ cookie }) => {
        const acceptedCategories = cookie.categories || [];

        // Update GA4 consent mode
        updateGAConsent(acceptedCategories);
      },

      // Replay a returning visitor's persisted consent on load. vanilla-cookieconsent
      // fires onConsent on the first consent action AND on every subsequent page load
      // when a valid consent cookie exists — this resumes full tracking immediately via
      // the shared gtag shim rather than waiting on the polling useEffect. On the first
      // Accept, onConsent + onFirstConsent both fire with identical categories; the
      // duplicate consent update is idempotent and harmless.
      onConsent: ({ cookie }) => {
        const acceptedCategories = cookie.categories || [];

        // Update GA4 consent mode
        updateGAConsent(acceptedCategories);
      },

      // Track consent-modal visibility so the footer shield can toggle it
      // (open ⇄ close). Scope strictly to the consent modal — the preferences
      // modal is not driven by the shield.
      onModalShow: ({ modalName }) => {
        if (modalName === 'consentModal') {
          setConsentModalOpen(true);
        }
      },
      onModalHide: ({ modalName }) => {
        if (modalName === 'consentModal') {
          setConsentModalOpen(false);
        }
      },

      // Respect DNT by defaulting to reject non-essential
      ...(dntEnabled && {
        categories: {
          ...cookieConsentConfig.categories,
          analytics: {
            ...cookieConsentConfig.categories.analytics,
            enabled: false,
          },
          marketing: {
            ...cookieConsentConfig.categories.marketing,
            enabled: false,
          },
        },
      }),
    });

    // Cleanup on unmount
    return () => {
      // vanilla-cookieconsent doesn't provide a cleanup method
      // The modal persists across route changes by design
    };
  }, []);

  // This component doesn't render any React elements
  // The cookie consent UI is injected by vanilla-cookieconsent
  return null;
};

// Export with display name for debugging
CookieConsentComponent.displayName = 'CookieConsent';

export { CookieConsentComponent as CookieConsent };
